import React, { useState, useMemo } from "react";
import CustomAudioPlayer, {
  TrackId,
} from "../../../../../components/CustomAudioPlayer";
import * as S from "./SeparationPanel.styles";
import { PanelContainer } from "../ConversationPanel/ConversationPanel.styles";
import { ContentContainer } from "../AudioAnalysisPanel/AudioAnalysisPanel.styles";
import {
  CommonActionBar,
  ActionType,
  DEFAULT_ACTION_STYLES,
} from "../../components/ActionBar";
import { useProcessVuStore } from "../../../../../store/processVuStore";
import { ProcessResult as VUResult } from "types/output_vu";

interface SeparationPanelProps {
  result: VUResult | null;
  onCopy: () => void;
  onShare: () => void;
  onEdit: () => void;
  onDownload: () => void;
  onLike: () => void;
  onDislike: () => void;
  feedbackStatus?: "positive" | "negative" | null;
}

const SeparationPanel: React.FC<SeparationPanelProps> = ({
  result,
  onCopy,
  onShare,
  onEdit,
  onDownload,
  onLike,
  onDislike,
  feedbackStatus,
}) => {
  const allAudioUrls = useMemo(() => {
    if (!result?.voice_separation) return {};

    const DOWNLOAD_URL = import.meta.env.VITE_DOWNLOAD_URL;
    const { separated_audios } = result.voice_separation;
    const urls: Record<string, string> = {};

    if (result.signal_processing?.media_path) {
      urls.all = `${DOWNLOAD_URL}/${result.signal_processing.media_path}`;
    }

    for (const key in separated_audios) {
      if (Object.prototype.hasOwnProperty.call(separated_audios, key)) {
        const path = (separated_audios as Record<string, string>)[key];
        if (path) {
          urls[key] = `${DOWNLOAD_URL}/${path}`;
        }
      }
    }
    return urls;
  }, [result]);

  const [selectedTracks, setSelectedTracks] = useState<TrackId[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [syncedTime, setSyncedTime] = useState(0);

  const handleSelect = (trackId: TrackId) => {
    if (!isPlaying) {
      setSyncedTime(0);
    }
    setSelectedTracks((prev) =>
      prev.includes(trackId)
        ? prev.filter((item) => item !== trackId)
        : [...prev, trackId]
    );
  };

  const handlePlayPause = () => {
    if (selectedTracks.length > 0) {
      setIsPlaying((prev) => !prev);
    }
  };

  const handleTimeUpdate = (trackId: TrackId, time: number) => {
    if (selectedTracks.length > 0 && trackId === selectedTracks[0]) {
      setSyncedTime(time);
    }
  };

  const handleEnded = (trackId: TrackId) => {
    if (selectedTracks.includes(trackId)) {
      setIsPlaying(false);
    }
  };

  const instrumentTracks = Object.entries(allAudioUrls).filter(
    ([key]) => key !== "speech" && !key.startsWith("SPEAKER")
  );

  const speakers = result?.output.speakerInfo.speakers || [];
  const speakerTracks = speakers.map((speakerId: string) => ({
    id: speakerId,
    url: allAudioUrls[speakerId], // URL이 있으면 할당, 없으면 undefined
  }));

  const leftActions = [
    {
      type: "copy" as ActionType,
      label: "복사",
      onClick: onCopy,
      ...DEFAULT_ACTION_STYLES.copy,
    },
    {
      type: "share" as ActionType,
      label: "공유",
      onClick: onShare,
      ...DEFAULT_ACTION_STYLES.share,
    },
    {
      type: "edit" as ActionType,
      label: "수정",
      onClick: onEdit,
      ...DEFAULT_ACTION_STYLES.edit,
    },
    {
      type: "like" as ActionType,
      label: "",
      onClick: onLike,
      ...DEFAULT_ACTION_STYLES.like,
      isActive: feedbackStatus === "positive",
    },
    {
      type: "dislike" as ActionType,
      label: "",
      onClick: onDislike,
      ...DEFAULT_ACTION_STYLES.dislike,
      isActive: feedbackStatus === "negative",
    },
  ];

  const rightActions = [
    {
      type: "download" as ActionType,
      label: "다운로드",
      onClick: onDownload,
      ...DEFAULT_ACTION_STYLES.download,
    },
  ];

  return (
    <PanelContainer>
      <ContentContainer>
        <S.PlayerContainer>
          {instrumentTracks.map(([trackId, url]) => (
            <CustomAudioPlayer
              key={trackId}
              trackId={trackId}
              audioUrl={url!}
              isSelected={selectedTracks.includes(trackId)}
              isPlaying={isPlaying}
              isAnyInstrumentSelected={selectedTracks.length > 0}
              syncedTime={syncedTime}
              onSelect={handleSelect}
              onPlayPause={handlePlayPause}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
            />
          ))}
          {speakerTracks.length > 0 && <S.Divider />}
          {speakerTracks.map((track) => (
            <CustomAudioPlayer
              key={track.id}
              trackId={track.id}
              audioUrl={track.url || ""}
              disabled={!track.url}
              isSelected={selectedTracks.includes(track.id)}
              isPlaying={isPlaying}
              isAnyInstrumentSelected={selectedTracks.length > 0}
              syncedTime={syncedTime}
              onSelect={handleSelect}
              onPlayPause={handlePlayPause}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
            />
          ))}
        </S.PlayerContainer>
      </ContentContainer>
      <CommonActionBar leftActions={leftActions} rightActions={rightActions} />
    </PanelContainer>
  );
};

export default SeparationPanel;
